import { createContext, useContext, useMemo, useState } from "react";
import {
  ApiChannelGroup,
  ApiChannelRule,
  ApiConditionGroup,
  ApiEffectivenessConditionGroup,
  ApiSessionEffectivenessConditionGroup,
  ApiAlertConditionGroup,
} from "@incendium/api";

export interface IChannelRuleOverride extends ApiChannelRule {
  custom: any;
  conversionId: any;
}

export interface IChannelGroupOverride extends ApiChannelGroup {
  conditions: IChannelRuleOverride[];
}

type TConditionGroup =
  | ApiConditionGroup
  | ApiEffectivenessConditionGroup
  | ApiSessionEffectivenessConditionGroup
  | IChannelGroupOverride
  | ApiAlertConditionGroup;

type TRowComponent =
  | ((groupIdx: number, rowIdx: number) => React.ReactElement)
  | undefined;

type TConditionGroupContext<T extends TConditionGroup = TConditionGroup> = {
  conditionGroups: T[];
  setConditionGroups: React.Dispatch<React.SetStateAction<T[]>>;
  rowComponent: TRowComponent;
  setRowComponent: React.Dispatch<React.SetStateAction<TRowComponent>>;
};

export const ConditionGroupContext = createContext<TConditionGroupContext<any>>(
  {
    conditionGroups: [],
    setConditionGroups: () => [],
    rowComponent: undefined,
    setRowComponent: () => {},
  },
);

export const useConditionGroupContext = <
  T extends TConditionGroup = TConditionGroup,
>() => useContext(ConditionGroupContext) as TConditionGroupContext<T>;

const ConditionGroupProvider = <T extends TConditionGroup>({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [conditionGroups, setConditionGroups] = useState<T[]>([]);
  const [rowComponent, setRowComponent] = useState<
    ((groupIdx: number, rowIdx: number) => React.ReactElement) | undefined
  >(undefined);

  const state = useMemo(
    () => ({
      conditionGroups,
      setConditionGroups,
      rowComponent,
      setRowComponent,
    }),
    [conditionGroups, setConditionGroups, rowComponent, setRowComponent],
  );

  return (
    <ConditionGroupContext.Provider value={state}>
      {children}
    </ConditionGroupContext.Provider>
  );
};

export default ConditionGroupProvider;
